/* eslint-disable @typescript-eslint/no-explicit-any */
import { Canvas, Object as FabricObject, TFiller } from 'fabric';

import { loadObjectsWithLayers } from './loadObjectsWithLayers';

/**
 * Changes the background color of the canvas and updates the corresponding metadata object
 *
 * @param canvas - Canvas instance in which to change the background
 * @param newColor - New background color (e.g., '#FFFFFF', 'rgb(255,255,255)', etc.)
 * @param addBackup - (optional) Function to create a backup of the state
 * @param canvasId - (optional) Canvas ID, required if addBackup is used
 * @returns Updated Canvas
 */
export const changeBackgroundColor = (
  canvas: Canvas,
  newColor: string | TFiller,
  addBackup?: (canvasId: string, state: Record<string, unknown>) => void,
  canvasId?: string,
): Canvas => {
  if (!canvas) {
    console.error('Canvas instance not provided');
    return canvas;
  }

  // Update the background color on the canvas
  canvas.set('backgroundColor', newColor);

  // Find the background object in canvas objects
  const backgroundObject = findBackgroundObject(canvas);

  if (backgroundObject) {
    // Update backgroundColor in the object with "background" category
    backgroundObject.set('backgroundColor', newColor);
    backgroundObject.set('backgroundImage', null);
  } else {
    console.warn('⚠️ Background object not found in canvas');
  }

  // Update the canvas

  const cfgToBackup = canvas.toJSON();
  cfgToBackup.backgroundImage = null;
  if (canvasId) {
    loadObjectsWithLayers(canvas, canvasId, cfgToBackup);
  } else {
    canvas.requestRenderAll();
  }
  // If addBackup function and canvas ID are provided, create a backup
  if (addBackup && canvasId) {
    addBackup(canvasId, cfgToBackup);
  }

  return canvas;
};

/**
 * Finds an object with the "background" category in the canvas
 *
 * @param canvas - Canvas instance to search
 * @returns Found object or null if no object is found
 */
export const findBackgroundObject = (canvas: Canvas): FabricObject | null => {
  const objects = canvas.getObjects();

  for (const obj of objects) {
    if (obj.category && obj.category.toLowerCase() === 'background') {
      return obj;
    }
  }

  return null;
};

/**
 * Gets the current background color from the background object
 *
 * @param canvas - Canvas instance
 * @returns Current background color or null if the object is not found
 */
export const getCurrentBackgroundColor = (canvas: Canvas): string | null => {
  const backgroundObject = findBackgroundObject(canvas);

  if (backgroundObject && backgroundObject.backgroundColor) {
    return backgroundObject.backgroundColor.toString();
  }

  // If the background object is not found, return the canvas background color
  return canvas.backgroundColor?.toString() || null;
};
